/**
 * requirements/index.js
 *
 * 모든 단과대별 이수요건을 모아 두 가지 포맷으로 제공한다.
 *
 * 새 포맷 (ALL_REQUIREMENTS):
 *   requiredCourses[].{ courseName, courseCode, type, credits, matchKeywords, ... }
 *
 * 구 포맷 (REQUIREMENT_DATA) — timetableUtils / deptUtils 역호환용:
 *   requiredSubjects[].{ subjectKey, displayName, category, credit }
 *
 * 기타 기존 exports (DEPARTMENTS, GRADES, ADMISSION_YEARS, CREDIT_RULES)는
 * requirementData.js 에서 이 파일을 통해 재공급한다.
 */

import { BUSINESS_REQUIREMENTS }    from './business.js'
import { ICT_REQUIREMENTS }         from './ict.js'
import { ENGINEERING_REQUIREMENTS } from './engineering.js'
import { ARTS_REQUIREMENTS }        from './arts.js'
import { HUMANITIES_REQUIREMENTS }  from './humanities.js'
import { HEALTH_REQUIREMENTS }      from './health.js'
import { ETC_REQUIREMENTS }         from './etc.js'
export  { COLLEGES, DEPT_META_MAP } from './colleges.js'

// ── 전체 이수요건 (새 포맷) ────────────────────────────────────────────────────
export const ALL_REQUIREMENTS = [
  ...BUSINESS_REQUIREMENTS,
  ...ICT_REQUIREMENTS,
  ...ENGINEERING_REQUIREMENTS,
  ...ARTS_REQUIREMENTS,
  ...HUMANITIES_REQUIREMENTS,
  ...HEALTH_REQUIREMENTS,
  ...ETC_REQUIREMENTS,
]

// ── 새 포맷 → 구 포맷 변환 ────────────────────────────────────────────────────
/**
 * requiredCourses[] (새 포맷) → requiredSubjects[] (구 포맷)
 * courseCode → subjectKey, type → category, credits → credit
 */
function toOldFormat(req) {
  return {
    id:            `${req.departmentId}-${req.admissionYear}-${req.grade}-${req.semester ?? 0}`,
    department:    req.departmentName,
    collegeName:   req.collegeName ?? null,
    grade:         req.grade,
    semester:      req.semester ?? null,
    admissionYear: req.admissionYear,
    status:        req.status ?? 'sample',
    note:          req.notes?.join(' · ') ?? '',
    requiredSubjects: (req.requiredCourses ?? []).map((c) => ({
      subjectKey:  c.courseCode ?? null,
      displayName: c.courseName,
      category:    c.type,
      credit:      c.credits,
      note:        '',
    })),
    electiveCategories:          ['교양선택', '핵심교양', '교양필수', '기초교양', '일반선택', '이러닝'],
    recommendedElectiveCredits:  9,
    maxCredit:                   21,
    minCredit:                   15,
  }
}

/**
 * REQUIREMENT_DATA — 구 포맷 배열 (timetableUtils / deptUtils 에서 직접 사용)
 *
 * requiredCourses가 비어 있는 스텁 항목은 제외한다.
 * (데이터 미입력 학과가 "학과에 이수요건 있음"으로 오판되지 않도록)
 */
export const REQUIREMENT_DATA = ALL_REQUIREMENTS
  .filter((r) => r.requiredCourses.length > 0)
  .map(toOldFormat)

// ── 매칭 헬퍼 (새 포맷용) ─────────────────────────────────────────────────────
/**
 * 학과/학년/입학년도로 새 포맷 이수요건 조회
 * semester가 null이면 해당 학년의 첫 번째 블록 반환
 */
export function getRequirement(departmentName, grade, admissionYear, semester = null) {
  const candidates = ALL_REQUIREMENTS.filter(
    (r) =>
      r.departmentName  === departmentName &&
      r.grade           === grade          &&
      (admissionYear == null || r.admissionYear === admissionYear)
  )
  if (!candidates.length) return null
  if (semester != null) {
    const bySem = candidates.find((r) => r.semester === semester)
    if (bySem) return bySem
  }
  return candidates[0]
}

/**
 * courseData 배열에서 requiredCourses 항목을 실제 분반 목록으로 매칭해 반환한다.
 *
 * 매칭 우선순위:
 *   1. courseCode (subjectKey) 정확 일치
 *   2. matchKeywords 중 하나가 과목명에 포함
 *
 * @param {object}   req        ALL_REQUIREMENTS 항목
 * @param {object[]} courseData COURSE_DATA 배열
 * @returns {{ course: object, sections: object[], matched: boolean }[]}
 */
export function matchRequiredSections(req, courseData) {
  if (!req?.requiredCourses?.length) return []
  return req.requiredCourses.map((rc) => {
    // 1차: courseCode 매칭
    let sections = rc.courseCode
      ? courseData.filter((c) => c.subjectKey === rc.courseCode)
      : []

    // 2차: matchKeywords 매칭
    if (!sections.length && rc.matchKeywords?.length) {
      sections = courseData.filter((c) =>
        rc.matchKeywords.some((kw) => c.name?.includes(kw))
      )
    }

    return {
      course:   rc,
      sections,
      matched:  sections.length > 0,
    }
  })
}

// ── 구 exports 재공급 (기존 코드 역호환) ─────────────────────────────────────
//
// requirementData.js 가 이 파일을 통해 아래 상수를 외부에 공급한다.
// timetable 컴포넌트들이 requirementData.js에서 직접 import하므로
// 여기서 정의 후 requirementData.js에서 re-export 한다.

import { COLLEGES } from './colleges.js'

/** UI 학과 선택 목록 (DepartmentSelectScreen 에서 사용) */
export const DEPARTMENTS = COLLEGES.flatMap((col) =>
  col.departments.map((dept) => ({
    id:      dept.departmentId,
    name:    dept.departmentName,
    icon:    dept.icon,
    hasData: dept.hasData,
  }))
)

export const GRADES          = [1, 2, 3, 4]
export const ADMISSION_YEARS = [2024, 2025, 2026]

// 수원대 수강신청 학점 규칙
export const CREDIT_RULES = {
  defaultMax:           21,
  defaultMin:           15,
  preRegistrationMax:   30,
  honorMax:             24,
  warningMax:           18,
}
